import axios, { AxiosResponse } from "axios";
import { URLS } from "../constants";
import i18n from "../i18n";
import logger from "../utils/logger";

const axiosInstance = axios.create({
  baseURL: URLS.CLIENT_URL,
  withCredentials: true,
  timeout: 30000,
});

axiosInstance.interceptors.request.use(
  (config) => {
    config.headers["Accept-Language"] = i18n.language;

    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      try {
        const userData = JSON.parse(authUser);
        if (userData?.token) {
          config.headers.Authorization = `Bearer ${userData.token}`;
        }
      } catch (e) {
        logger.error("Error parsing authUser data:", e);
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - Token expired or invalid
    if (error.response?.status === 401 && !originalRequest._retry) {
      const currentPath = window.location.pathname;

      // Avoid redirect loop if already on auth pages or refresh endpoint
      if (
        currentPath === "/login" ||
        currentPath === "/register" ||
        currentPath === "/recover-password" ||
        originalRequest.url?.includes("/auth/refresh")
      ) {
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh token
        await axiosInstance.post("/api/auth/refresh", {});

        isRefreshing = false;
        processQueue(null, null);

        // Retry original request
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);

        // Clear auth data
        localStorage.removeItem("authUser");
        localStorage.removeItem("token");

        // Redirect to login
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export interface AppConfigField {
  key: string;
  value: string;
}

export interface AppMedia {
  id: string;
  url: string;
  type: string;
}

export interface Category {
  id: string;
  name: string;
}

export interface App {
  id: string;
  code: string;
  name: string;
  internal_name: string;
  latest_version: string;
  description: string;
  icon_url: string;
  category: string;
  status: string;
  metadata?: AppConfigField[];
  created_at: string;
  updated_at: string;
  categories?: Category[];
  media?: AppMedia[];
}

export interface BusinessType {
  id: string;
  code: string;
  industry_id: string;
  name_en: string;
  name_pt: string;
  name_es: string;
  created_at: string;
  updated_at: string;
  apps: App[];
}

export interface IndustryWithBusinessTypes {
  id: string;
  code: string;
  name: string;
  business_types: BusinessType[];
}

export const getIndustries = (): Promise<
  AxiosResponse<IndustryWithBusinessTypes[]>
> => axiosInstance.get("/api/industries");
